import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  signal,
  HostBinding,
  ChangeDetectionStrategy,
  HostListener,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule } from '@angular/router';
import { resolveProductImage } from '../../../core/utils/image.util';
import { buildDescriptiveBoatPath } from '../../../core/utils/boat-url.util';
import { UrlService } from '../../../core/services/shared/url.service';
import { ListingShareService } from '../../../core/services/shared/listing-share.service';

@Component({
  selector: 'app-product-card',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './product-card.component.html',
  styleUrls: ['./product-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductCardComponent {
  @Input() product!: any;
  @Input() variant: 'default' | 'compact' = 'default';
  @Input() isListView = false;
  @Input() imageSize: 'thumbnail' | 'medium' | 'original' = 'thumbnail';

  @Output() interested = new EventEmitter<any>();
  private urlService = inject(UrlService);
  private listingShare = inject(ListingShareService);
  shareStatus = signal('');
  private platformId = inject(PLATFORM_ID);
  isShareExpanded = signal(false);
  isSharePopupOpen = signal(false);

  @HostBinding('class.compact') get isCompact() {
    return this.variant === 'compact';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const isShareBtn = target.closest('.btn-share-trigger');
    const isShareDropdown = target.closest('.share-dropdown');

    if (!isShareBtn && !isShareDropdown && this.isSharePopupOpen()) {
      this.isSharePopupOpen.set(false);
    }
  }

  get imageUrl(): string {
    return resolveProductImage(this.product, this.imageSize);
  }

  get productName(): string {
    const p = this.product;
    return p.name || p.boat?.boatName || p.merchandise?.merchandiseName || 'Unnamed Product';
  }

  get productDescription(): string {
    const p = this.product;
    const rawDesc =
      p.description || p.boat?.boatDescription || p.merchandise?.merchandiseDescription || '';

    // JUNK TEXT FILTER: If description is just a few characters or matches junk patterns like "sdf"
    const cleanDesc = rawDesc.trim();
    if (cleanDesc.length < 5 || /^[asdf\s]+$/i.test(cleanDesc)) {
      return '';
    }
    return rawDesc;
  }

  get productTypeLabel(): 'boat' | 'merchandise' | 'other' {
    const p = this.product;
    if (!p) return 'other';
    const typeName = (
      p.productType?.statusName ||
      p.productType?.typeName ||
      p.productType?.productTypeName ||
      ''
    ).toLowerCase();
    if (typeName.includes('boat') || typeName.includes('yacht') || p.boat || p.type === 'boat')
      return 'boat';
    if (typeName.includes('merch') || p.merchandise || p.type === 'merchandise')
      return 'merchandise';
    return 'other';
  }

  get boatTypeName(): string {
    const p = this.product;
    return p.boatTypeName || p.boat?.boatTypeName || p.boat?.boatType?.boatTypeName || 'Boat';
  }

  handleImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/no-image.png';
  }
  get statusLabel(): string {
    const s = this.product.status;
    if (typeof s === 'string') return s;
    return s?.statusName ?? this.product.statusName ?? 'Not Listed';
  }

  get statusClass(): string {
    const s = this.statusLabel.toLowerCase();
    if (s.includes('available')) return 'available';
    if (s.includes('sold')) return 'sold';
    if (s.includes('reserved') || s.includes('pending') || s.includes('hold')) return 'pending';
    return 'other';
  }

  getDetailLink(): any[] {
    const p = this.product;
    if (this.productTypeLabel === 'merchandise') {
      return ['/merchandises', p.productId || p.id];
    }
    if (this.productTypeLabel === 'boat') {
      const path = buildDescriptiveBoatPath({
        listingId: p.productId || p.id,
        yachtType: p.boatTypeName || p.boat?.boatType?.boatTypeName,
        year: p.details?.year ?? p.year ?? p.boat?.boatYear,
        displayName: p.name || p.boat?.boatName,
        listingReference: p.listingReference,
      });
      if (path) return ['/boats', ...path.split('/').slice(2)];
      return ['/boats', p.productId || p.id];
    }
    return ['/yachts', p.productId || p.id];
  }

  getRouterState() {
    return {
      productTypeId: this.product.productTypeId,
    };
  }

  // onImgError removed

  onInterested(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.interested.emit(this.product);
  }

  async onShare(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    const pId = this.product.productId || this.product.id;
    const type = this.productTypeLabel === 'merchandise' ? 'merchandises' : 'yachts';
    const url = this.urlService.getFrontendShareUrl(pId, type);

    try { await this.listingShare.copy(url); this.shareStatus.set('Listing link copied.'); }
    catch { this.shareStatus.set('Could not copy the link. Please try again.'); }
  }

  isFavorite(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    const pId = this.product.productId || this.product.id;
    if (!pId) return false;
    let favs: string[] = [];
    try {
      favs = JSON.parse(localStorage.getItem('seger_favorites') || '[]');
    } catch {}
    return favs.includes(String(pId));
  }

  toggleFavorite(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!isPlatformBrowser(this.platformId)) return;
    const pId = String(this.product.productId || this.product.id);
    if (!pId) return;

    let favs: string[] = [];
    try {
      favs = JSON.parse(localStorage.getItem('seger_favorites') || '[]');
    } catch {}

    if (favs.includes(pId)) {
      favs = favs.filter((id) => id !== pId);
    } else {
      favs.push(pId);
    }
    localStorage.setItem('seger_favorites', JSON.stringify(favs));
  }

  shareTo(platform: 'facebook' | 'twitter' | 'whatsapp' | 'instagram', event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const pId = this.product.productId || this.product.id;
    if (!pId) return;

    const type = this.productTypeLabel === 'merchandise' ? 'merchandises' : 'yachts';
    const shareUrl = this.urlService.getFrontendShareUrl(pId, type);
    const encodedUrl = encodeURIComponent(shareUrl);
    const title = encodeURIComponent(this.productName);

    let finalUrl = '';
    switch (platform) {
      case 'facebook':
        if (this.productTypeLabel === 'boat') {
          this.shareStatus.set('Copying caption for Facebook...');
          void this.listingShare.facebook(shareUrl, { title: this.productName,
            description: this.productDescription, price: this.product.price,
            path: this.getDetailLink().join('/') })
            .then(message => this.shareStatus.set(message));
          return;
        }
        finalUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter':
        finalUrl = `https://x.com/intent/tweet?url=${encodedUrl}&text=${title}`;
        break;
      case 'whatsapp':
        finalUrl = `https://wa.me/?text=${title}%20${encodedUrl}`;
        break;
      case 'instagram':
        // Best effort for Instagram: Copy link and alert
        if (isPlatformBrowser(this.platformId) && navigator.clipboard) {
          navigator.clipboard.writeText(shareUrl).then(() => {
            alert('Link copied! Open Instagram to share in your story or bio.');
          });
        }
        this.isShareExpanded.set(false);
        return;
    }

    if (finalUrl && isPlatformBrowser(this.platformId)) {
      window.open(finalUrl, '_blank', 'noopener,noreferrer');
    }
    this.isShareExpanded.set(false);
    this.isSharePopupOpen.set(false);
  }

}
